import React, { useEffect, useRef, useState } from 'react'

import { Box, IconButton, Modal, Paper, Slide, Theme } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'

import { AddVendor, ExistingVendor } from './Containers'
import {
    ApartmentVendor,
    AreaConfig,
    IdBoolMap,
    ListVendor,
    Service,
    UnitConfig,
} from '../../../models'
import { WorkforceFormState } from '../Workforce'
import { StepFindVendor, VendorSearchResult } from './Components/StepFindVendor'
import { axiosInstance } from '../../../helpers'
import {
    CompanyDetailActionThunk,
    CreateVendorRequest,
    UpdateVendorRequest,
    UpdateVendorServicesRequest,
    VendorAndContractThunk,
} from '../../../store'
import { CreateServiceActionThunk } from '../../../store/service'
import { CreateServiceRequest } from '../../../models'

interface Props {
    vendorList: ListVendor[]
    theme: Theme
    formState: WorkforceFormState
    serviceList: Service[]
    unitConfigs: UnitConfig[]
    areaConfigs: AreaConfig[]
    apartmentVendorMap: Record<number, ApartmentVendor>
    onClose: () => void
    updateVendor: (req: UpdateVendorRequest) => VendorAndContractThunk
    updateVendorServices: (
        req: UpdateVendorServicesRequest,
    ) => CompanyDetailActionThunk
    createVendor: (req: CreateVendorRequest) => VendorAndContractThunk
    createService: (req: CreateServiceRequest) => CreateServiceActionThunk
    setApartmentVendorMap: (map: Record<number, ApartmentVendor>) => void
}

export const VendorModal = (props: Props) => {
    const { onClose, theme, vendorList, formState } = props

    const [modalState, setModalState] = useState<VendorModalState>(
        defaultModalState,
    )

    const vendorModalFs = formState.modal.vendorModal
    const { open, vendorId } = vendorModalFs

    const handleVendorFound = (vendor: ListVendor) => {
        const inWs =
            vendorList.find((vnd) => vnd.id === vendor.id) !== undefined

        setModalState({
            ...modalState,
            activeVendorId: vendor.id,
            mode: inWs ? 'edit' : 'add-to-workspace',
            addToWsVendor: vendor,
            searchEmail: vendor.email ?? modalState.searchEmail,
        })
    }

    const handleVendorNotFound = () => {
        setModalState({
            ...modalState,
            activeVendorId: -1,
            mode: 'create-new',
            searchComplete: true,
        })
    }

    const handleSelectVendor = (result: VendorSearchResult) => {
        const localVendor = vendorList.find((vnd) => vnd.id === result.id)
        if (localVendor) {
            handleVendorFound(localVendor)
            return
        }

        axiosInstance
            .post('company/vendor/find_vendor_by_id/', { id: result.id })
            .then((res) => handleVendorFound(res.data.vendor))
            .catch(handleVendorNotFound)
    }

    const handleCreateNew = (name: string, email: string) => {
        setModalState({
            ...modalState,
            activeVendorId: -1,
            mode: 'create-new',
            searchComplete: true,
            searchName: name,
            searchEmail: email,
        })
    }

    const handleSearchEzid = (id: number) => {
        axiosInstance
            .post('company/vendor/find_vendor_by_id/', { id })
            .then((res) => handleVendorFound(res.data.vendor))
            .catch(handleVendorNotFound)
    }

    const handleCleanup = () => {
        setModalState(defaultModalState)
    }

    const pricingCloseGuardRef = useRef<(() => boolean) | null>(null)
    const setPricingCloseGuard = (guard: (() => boolean) | null) => {
        pricingCloseGuardRef.current = guard
    }

    const requestClose = () => {
        if (pricingCloseGuardRef.current?.()) {
            return
        }
        onClose()
    }

    useEffect(() => {
        if (open) {
            const vendor = vendorList.find((vnd) => vnd.id === vendorId)

            if (vendor !== undefined) {
                setModalState({
                    ...modalState,
                    activeVendorId: vendorId,
                    mode: 'edit',
                    searchComplete: true,
                })
            } else {
                setModalState(defaultModalState)
            }
        } else {
            handleCleanup()
        }
    }, [open])

    const activeVendor = vendorList.find(
        (vnd) => vnd.id === modalState.activeVendorId,
    )

    return (
        <Modal
            open={open}
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
            onClose={requestClose}
        >
            <Slide direction="up" in={open}>
                <Paper
                    style={{
                        display: 'flex',
                        overflow: 'hidden',
                        flexDirection: 'column',
                        justifyContent: 'flex-start',
                        outline: 'none',
                        borderRadius: theme.shape.borderRadius,
                        position: 'relative',
                    }}
                >
                    <IconButton
                        onClick={requestClose}
                        style={{
                            position: 'absolute',
                            top: theme.spacing(1),
                            right: theme.spacing(1),
                            zIndex: 1,
                        }}
                        size="small"
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                    {modalState.mode === 'search' && (
                        <StepFindVendor
                            theme={theme}
                            onSelectVendor={handleSelectVendor}
                            onCreateNew={handleCreateNew}
                            onSearchEzid={handleSearchEzid}
                        />
                    )}

                    {modalState.mode === 'create-new' && (
                        <AddVendor
                            vendor={null}
                            searchName={modalState.searchName}
                            searchEmail={modalState.searchEmail}
                            {...props}
                            setPricingCloseGuard={setPricingCloseGuard}
                        />
                    )}

                    {modalState.mode === 'add-to-workspace' && (
                        <AddVendor
                            vendor={activeVendor ?? modalState.addToWsVendor}
                            searchEmail={modalState.searchEmail}
                            {...props}
                            setPricingCloseGuard={setPricingCloseGuard}
                        />
                    )}

                    {modalState.mode === 'edit' && (
                        <ExistingVendor
                            vendor={activeVendor!}
                            {...props}
                            setPricingCloseGuard={setPricingCloseGuard}
                        />
                    )}
                </Paper>
            </Slide>
        </Modal>
    )
}

export type VendorModalState = {
    mode: 'create-new' | 'add-to-workspace' | 'edit' | 'search'
    activeVendorId: number
    searchComplete: boolean
    searchName: string
    searchEmail: string
    addToWsVendor: ListVendor | null
}

const defaultModalState: VendorModalState = {
    mode: 'search',
    activeVendorId: -1,
    searchComplete: false,
    searchName: '',
    searchEmail: '',
    addToWsVendor: null,
}
